import React, { useState, useEffect, useRef } from 'react';
import {
  Folder,
  File,
  Upload,
  Download,
  Trash2,
  Plus,
  Search,
  FolderPlus,
  FileText,
  Image,
  Video,
  Music,
  Archive,
  MoreVertical,
  ChevronRight,
  Home,
  Share2,
  Building2,
  Users,
  Lock,
  Eye,
  X,
  Loader2
} from 'lucide-react';
import api from '../services/api';

const FILE_ICONS = {
  pdf: { icon: FileText, color: 'text-red-500' },
  doc: { icon: FileText, color: 'text-blue-500' },
  docx: { icon: FileText, color: 'text-blue-500' },
  xls: { icon: FileText, color: 'text-green-500' },
  xlsx: { icon: FileText, color: 'text-green-500' },
  jpg: { icon: Image, color: 'text-purple-500' },
  jpeg: { icon: Image, color: 'text-purple-500' },
  png: { icon: Image, color: 'text-purple-500' },
  gif: { icon: Image, color: 'text-purple-500' },
  mp4: { icon: Video, color: 'text-pink-500' },
  avi: { icon: Video, color: 'text-pink-500' },
  mp3: { icon: Music, color: 'text-yellow-500' },
  zip: { icon: Archive, color: 'text-orange-500' },
  rar: { icon: Archive, color: 'text-orange-500' },
  default: { icon: File, color: 'text-gray-500' }
};

export default function FileManager({ user, departments = [] }) {
  const [currentFolder, setCurrentFolder] = useState('root');
  const [currentPath, setCurrentPath] = useState([{ id: 'root', name: 'Archivos' }]);
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [shareWithAll, setShareWithAll] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' o 'list'
  const [contextMenu, setContextMenu] = useState(null);
  const [editingFolder, setEditingFolder] = useState(null);
  const [editFolderName, setEditFolderName] = useState('');
  const [showShareModal, setShowShareModal] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);

  const isAdmin = ['superadmin', 'admin'].includes(user?.role);

  useEffect(() => {
    loadContent();
  }, [currentFolder]);

  const loadContent = async () => {
    setLoading(true);
    try {
      const folderPath = currentFolder === 'root' ? '/' : currentFolder;
      const response = await api.get(`/api/files?folder=${folderPath}`);

      // El backend devuelve files directamente
      const filesData = response.data.files || [];

      // Separar carpetas y archivos
      const foldersData = filesData.filter(f => f.file_type === 'folder');
      const actualFiles = filesData.filter(f => f.file_type !== 'folder');

      setFolders(foldersData);
      setFiles(actualFiles);
    } catch (error) {
      console.error('Error cargando archivos:', error);
      // Si la API falla, mostrar estructura por defecto
      if (currentFolder === 'root') {
        setFolders(getDefaultFolders());
        setFiles([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const getDefaultFolders = () => {
    if (currentFolder === 'root') {
      const defaultFolders = [
        { id: 'shared', name: 'Compartido', icon: 'share', type: 'shared', description: 'Archivos compartidos de la empresa' }
      ];

      // Agregar carpetas de departamentos
      departments.forEach(dept => {
        defaultFolders.push({
          id: `dept-${dept.id}`,
          name: dept.name,
          icon: 'department',
          type: 'department',
          department_id: dept.id,
          description: `Archivos del departamento ${dept.name}`
        });
      });

      // Solo admins ven la carpeta de franquiciados
      if (isAdmin) {
        defaultFolders.push({
          id: 'franquiciados',
          name: 'Franquiciados',
          icon: 'franquiciados',
          type: 'franquiciados',
          description: 'Documentos para franquiciados (solo descarga)'
        });
      }

      return defaultFolders;
    }
    return [];
  };

  const navigateToFolder = (folder) => {
    setCurrentFolder(folder.id);
    setCurrentPath([...currentPath, { id: folder.id, name: folder.name }]);
    setSelectedItems([]);
  };

  const navigateToPath = (index) => {
    const newPath = currentPath.slice(0, index + 1);
    setCurrentPath(newPath);
    setCurrentFolder(newPath[newPath.length - 1].id);
    setSelectedItems([]);
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;

    try {
      await api.post('/api/files/folders', {
        name: newFolderName,
        parent_id: currentFolder,
        shared_with_all: shareWithAll
      });
      setNewFolderName('');
      setShareWithAll(false);
      setShowNewFolder(false);
      loadContent();
    } catch (error) {
      console.error('Error creando carpeta:', error);
      // Crear localmente por ahora
      const newFolder = {
        id: `folder-${Date.now()}`,
        name: newFolderName,
        type: 'folder',
        shared_with_all: shareWithAll,
        created_at: new Date().toISOString()
      };
      setFolders([...folders, newFolder]);
      setNewFolderName('');
      setShareWithAll(false);
      setShowNewFolder(false);
    }
  };

  const handleRenameFolder = async () => {
    if (!editFolderName.trim() || !editingFolder) return;

    try {
      await api.put(`/api/files/folders/${editingFolder.id}`, {
        name: editFolderName
      });
      setEditingFolder(null);
      setEditFolderName('');
      loadContent();
    } catch (error) {
      console.error('Error renombrando carpeta:', error);
    }
  };

  const handleDeleteFolder = async (folder) => {
    if (!window.confirm(`¿Eliminar la carpeta "${folder.name}"? Se eliminará todo su contenido.`)) return;

    try {
      await api.delete(`/api/files/folders/${folder.id}`);
      loadContent();
    } catch (error) {
      console.error('Error eliminando carpeta:', error);
    }
  };

  const handleShareFolder = async (folder, sharedWithAll) => {
    try {
      await api.put(`/api/files/folders/${folder.id}/share`, {
        shared_with_all: sharedWithAll
      });
      setShowShareModal(null);
      loadContent();
    } catch (error) {
      console.error('Error compartiendo carpeta:', error);
    }
  };

  const handleFileUpload = async (e, droppedFiles = null) => {
    const selectedFiles = droppedFiles || e.target.files;
    if (!selectedFiles || !selectedFiles.length) return;

    setUploading(true);
    try {
      const formData = new FormData();

      // Procesar archivos (puede incluir archivos de carpetas)
      const fileArray = Array.from(selectedFiles);
      console.log(`Subiendo ${fileArray.length} archivos...`);

      for (let file of fileArray) {
        formData.append('files', file);

        // Si el archivo tiene una ruta (webkitRelativePath), preservarla
        if (file.webkitRelativePath) {
          console.log('Archivo con ruta:', file.webkitRelativePath);
        }
      }

      // Enviar la ruta actual como folder_path
      const folderPath = currentFolder === 'root' ? '/' : currentFolder;
      formData.append('folder_path', folderPath);

      const response = await api.post('/api/files/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      console.log('Archivos subidos:', response.data);
      alert(`✅ ${response.data.files?.length || fileArray.length} archivo(s) subido(s) correctamente`);
      loadContent();
    } catch (error) {
      console.error('Error subiendo archivos:', error);
      alert('❌ Error al subir archivos: ' + (error.response?.data?.error || error.message));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (folderInputRef.current) folderInputRef.current.value = '';
    }
  };

  // Drag & Drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles && droppedFiles.length > 0) {
      handleFileUpload(null, droppedFiles);
    }
  };

  const handleDownload = async (file) => {
    try {
      const response = await api.get(`/api/files/${file.id}/download`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', file.name);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error descargando archivo:', error);
      alert('Funcion de descarga pendiente de implementar en el servidor');
    }
  };

  const handleDelete = async (items) => {
    if (!window.confirm(`¿Eliminar ${items.length} elemento(s)?`)) return;

    try {
      await Promise.all(items.map(item =>
        api.delete(`/api/files/${item.id}`)
      ));
      loadContent();
      setSelectedItems([]);
    } catch (error) {
      console.error('Error eliminando:', error);
      // Eliminar localmente
      setFiles(files.filter(f => !items.find(i => i.id === f.id)));
      setFolders(folders.filter(f => !items.find(i => i.id === f.id)));
      setSelectedItems([]);
    }
  };

  const getFileIcon = (filename) => {
    const ext = filename.split('.').pop().toLowerCase();
    return FILE_ICONS[ext] || FILE_ICONS.default;
  };

  const formatSize = (bytes) => {
    if (!bytes) return '-';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getFolderIcon = (folder) => {
    switch (folder.icon || folder.type) {
      case 'share':
      case 'shared':
        return <Share2 className="w-6 h-6 text-blue-500" />;
      case 'department':
        return <Users className="w-6 h-6 text-indigo-500" />;
      case 'franquiciados':
        return <Building2 className="w-6 h-6 text-green-500" />;
      default:
        return <Folder className="w-6 h-6 text-yellow-500" />;
    }
  };

  const canUpload = () => {
    // En carpeta franquiciados solo admin puede subir
    if (currentPath.some(p => p.id === 'franquiciados')) {
      return isAdmin;
    }
    return true;
  };

  const canDelete = () => {
    // En carpeta franquiciados solo admin puede eliminar
    if (currentPath.some(p => p.id === 'franquiciados')) {
      return isAdmin;
    }
    return true;
  };

  const filteredFolders = folders.filter(f =>
    f.name.toLowerCase().includes(search.toLowerCase())
  );

  const filteredFiles = files.filter(f =>
    f.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Header */}
      <div className="p-4 border-b">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Folder className="w-6 h-6 text-blue-600" />
            <h1 className="text-xl font-bold">Archivos</h1>
          </div>
          <div className="flex items-center gap-2">
            {canUpload() && (
              <>
                <button
                  onClick={() => setShowNewFolder(true)}
                  className="flex items-center gap-2 px-3 py-2 border rounded-lg hover:bg-gray-50"
                >
                  <FolderPlus className="w-4 h-4" />
                  <span className="hidden sm:inline">Nueva Carpeta</span>
                </button>
                <button
                  onClick={() => folderInputRef.current?.click()}
                  disabled={uploading}
                  className="flex items-center gap-2 px-3 py-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50"
                  title="Subir carpeta completa"
                >
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Folder className="w-4 h-4" />}
                  <span className="hidden sm:inline">Subir Carpeta</span>
                </button>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  <span className="hidden sm:inline">Subir Archivos</span>
                </button>
              </>
            )}
            {/* Input para archivos individuales */}
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFileUpload}
              className="hidden"
            />
            {/* Input para carpetas completas */}
            <input
              ref={folderInputRef}
              type="file"
              webkitdirectory=""
              directory=""
              multiple
              onChange={handleFileUpload}
              className="hidden"
            />
          </div>
        </div>

        {/* Breadcrumb */}
        <div className="flex items-center gap-1 text-sm mb-3 overflow-x-auto">
          {currentPath.map((item, index) => (
            <React.Fragment key={item.id}>
              {index > 0 && <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />}
              <button
                onClick={() => navigateToPath(index)}
                className={`px-2 py-1 rounded hover:bg-gray-100 whitespace-nowrap ${
                  index === currentPath.length - 1 ? 'font-medium text-blue-600' : 'text-gray-600'
                }`}
              >
                {index === 0 ? <Home className="w-4 h-4" /> : item.name}
              </button>
            </React.Fragment>
          ))}
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar archivos y carpetas..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg"
          />
        </div>
      </div>

      {/* Content - Drag & Drop Zone */}
      <div
        className={`flex-1 overflow-y-auto p-4 transition-colors ${
          isDragging ? 'bg-blue-50 border-2 border-dashed border-blue-400' : ''
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {isDragging && canUpload() && (
          <div className="absolute inset-0 flex items-center justify-center bg-blue-50/90 z-10 pointer-events-none">
            <div className="text-center">
              <Upload className="w-16 h-16 text-blue-600 mx-auto mb-4 animate-bounce" />
              <p className="text-xl font-medium text-blue-600">Suelta aquí para subir</p>
              <p className="text-sm text-blue-500 mt-2">Archivos o carpetas completas</p>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center h-40">
            <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
          </div>
        ) : filteredFolders.length === 0 && filteredFiles.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-40 text-gray-500">
            <Upload className="w-12 h-12 mb-2 opacity-50" />
            <p className="font-medium">Carpeta vacia</p>
            {canUpload() && (
              <>
                <p className="text-sm mt-1">Arrastra archivos aquí o usa los botones</p>
                <p className="text-xs mt-1 text-gray-400">También puedes arrastrar carpetas completas</p>
              </>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {/* Carpetas */}
            {filteredFolders.length > 0 && (
              <div>
                <h3 className="text-xs font-medium text-gray-500 uppercase mb-2">Carpetas</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                  {filteredFolders.map(folder => (
                    <div
                      key={folder.id}
                      className="relative p-4 border rounded-lg hover:bg-gray-50 cursor-pointer transition group"
                    >
                      {/* Menú 3 puntitos */}
                      {isAdmin && folder.type !== 'shared' && folder.type !== 'departments' && folder.type !== 'franquiciados' && (
                        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition z-10">
                          <div className="relative">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setContextMenu(contextMenu === folder.id ? null : folder.id);
                              }}
                              className="p-1 hover:bg-white rounded"
                            >
                              <MoreVertical className="w-4 h-4 text-gray-600" />
                            </button>
                            {contextMenu === folder.id && (
                              <div className="absolute right-0 mt-1 bg-white border rounded-lg shadow-lg py-1 w-40">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setEditingFolder(folder);
                                    setEditFolderName(folder.name);
                                    setContextMenu(null);
                                  }}
                                  className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                                >
                                  <FileText className="w-4 h-4" />
                                  Renombrar
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setShowShareModal(folder);
                                    setContextMenu(null);
                                  }}
                                  className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                                >
                                  <Share2 className="w-4 h-4" />
                                  Permisos
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteFolder(folder);
                                    setContextMenu(null);
                                  }}
                                  className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 text-red-600"
                                >
                                  <Trash2 className="w-4 h-4" />
                                  Eliminar
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                      <div onClick={() => navigateToFolder(folder)} className="flex flex-col items-center text-center">
                        {getFolderIcon(folder)}
                        <span className="mt-2 text-sm font-medium truncate w-full">{folder.name}</span>
                        {folder.description && (
                          <span className="text-xs text-gray-400 truncate w-full">{folder.description}</span>
                        )}
                        {folder.shared_with_all && (
                          <span className="mt-1 text-xs text-blue-500 flex items-center gap-1">
                            <Users className="w-3 h-3" /> Compartida
                          </span>
                        )}
                        {folder.type === 'franquiciados' && (
                          <span className="mt-1 text-xs text-orange-500 flex items-center gap-1">
                            <Eye className="w-3 h-3" /> Solo lectura
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Archivos */}
            {filteredFiles.length > 0 && (
              <div>
                <h3 className="text-xs font-medium text-gray-500 uppercase mb-2">Archivos</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                  {filteredFiles.map(file => {
                    const fileIcon = getFileIcon(file.name);
                    const FileIcon = fileIcon.icon;

                    return (
                      <div
                        key={file.id}
                        className="p-4 border rounded-lg hover:bg-gray-50 cursor-pointer transition group relative"
                      >
                        <div className="flex flex-col items-center text-center">
                          <FileIcon className={`w-8 h-8 ${fileIcon.color}`} />
                          <span className="mt-2 text-sm font-medium truncate w-full" title={file.name}>
                            {file.name}
                          </span>
                          <span className="text-xs text-gray-400">{formatSize(file.size)}</span>
                        </div>

                        {/* Acciones */}
                        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition flex gap-1">
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDownload(file); }}
                            className="p-1 bg-white rounded shadow hover:bg-gray-100"
                            title="Descargar"
                          >
                            <Download className="w-4 h-4 text-blue-600" />
                          </button>
                          {canDelete() && (
                            <button
                              onClick={(e) => { e.stopPropagation(); handleDelete([file]); }}
                              className="p-1 bg-white rounded shadow hover:bg-gray-100"
                              title="Eliminar"
                            >
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal Nueva Carpeta */}
      {showNewFolder && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-sm mx-4 p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold">Nueva Carpeta</h3>
              <button onClick={() => setShowNewFolder(false)} className="p-1 hover:bg-gray-100 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            <input
              type="text"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              placeholder="Nombre de la carpeta"
              className="w-full p-2 border rounded-lg mb-3"
              autoFocus
              onKeyDown={(e) => e.key === 'Enter' && handleCreateFolder()}
            />
            <label className="flex items-center gap-2 mb-4 cursor-pointer">
              <input
                type="checkbox"
                checked={shareWithAll}
                onChange={(e) => setShareWithAll(e.target.checked)}
                className="w-4 h-4 text-blue-600 rounded"
              />
              <span className="text-sm text-gray-700">Compartir con todos los usuarios</span>
            </label>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowNewFolder(false)}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleCreateFolder}
                disabled={!newFolderName.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                Crear
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Renombrar */}
      {editingFolder && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-sm mx-4 p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold">Renombrar Carpeta</h3>
              <button onClick={() => setEditingFolder(null)} className="p-1 hover:bg-gray-100 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            <input
              type="text"
              value={editFolderName}
              onChange={(e) => setEditFolderName(e.target.value)}
              placeholder="Nuevo nombre"
              className="w-full p-2 border rounded-lg mb-4"
              autoFocus
              onKeyDown={(e) => e.key === 'Enter' && handleRenameFolder()}
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setEditingFolder(null)}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleRenameFolder}
                disabled={!editFolderName.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                Renombrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Permisos */}
      {showShareModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-sm mx-4 p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold">Permisos de Carpeta</h3>
              <button onClick={() => setShowShareModal(null)} className="p-1 hover:bg-gray-100 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-4">{showShareModal.name}</p>
            <div className="space-y-3">
              <button
                onClick={() => handleShareFolder(showShareModal, true)}
                className={`w-full p-3 border rounded-lg text-left hover:bg-gray-50 ${
                  showShareModal.shared_with_all ? 'border-blue-500 bg-blue-50' : ''
                }`}
              >
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  <div>
                    <div className="font-medium">Compartida con todos</div>
                    <div className="text-xs text-gray-500">Todos los usuarios pueden ver y descargar</div>
                  </div>
                </div>
              </button>
              <button
                onClick={() => handleShareFolder(showShareModal, false)}
                className={`w-full p-3 border rounded-lg text-left hover:bg-gray-50 ${
                  !showShareModal.shared_with_all ? 'border-blue-500 bg-blue-50' : ''
                }`}
              >
                <div className="flex items-center gap-2">
                  <Lock className="w-5 h-5" />
                  <div>
                    <div className="font-medium">Solo yo</div>
                    <div className="text-xs text-gray-500">Solo tú puedes acceder a esta carpeta</div>
                  </div>
                </div>
              </button>
            </div>
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => setShowShareModal(null)}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
